import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Recordings: what the Zflix DVR has recorded, is recording, and will record (one-offs and series).
 * Recordings are made from Live TV (guide: a future program -> Record; full-screen player -> Record).
 */
const fmtDay = (t) => {
  try {
    const d = new Date(t);
    const today = new Date();
    const tomorrow = new Date(Date.now() + 86400000);
    const day = d.toDateString() === today.toDateString() ? 'Today' : d.toDateString() === tomorrow.toDateString() ? 'Tomorrow' : d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
    return `${day} ${d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`;
  } catch (_) {
    return '';
  }
};
const fmtLen = (s) => {
  const m = Math.round((s || 0) / 60);
  return m >= 60 ? `${Math.floor(m / 60)} h ${m % 60} min` : `${m} min`;
};
const gb = (b) => `${((b || 0) / 1024 ** 3).toFixed(1)} GB`;

const CSS = `
.rc-card { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 14px; padding: 12px 14px; margin-bottom: 10px; border-radius: 14px; border: 1px solid rgba(255,255,255,0.09); background-color: var(--surface); transition: border-color .15s, transform .15s; }
.rc-card:focus-within { border-color: var(--accent); transform: scale(1.01); }
.rc-card.is-rec { border-color: rgba(229,9,20,0.55); }
.rc-open { flex: 1 1 280px; min-width: 0; display: flex; align-items: center; gap: 14px; text-align: left; background: transparent; border: none; color: var(--text); padding: 2px; border-radius: 10px; }
.rc-open:focus { outline: 3px solid #fff; outline-offset: 2px; }
.rc-art { flex: none; display: flex; align-items: center; justify-content: center; width: 76px; height: 56px; }
.rc-chan { width: 76px; height: 56px; box-sizing: border-box; padding: 6px; object-fit: contain; border-radius: 10px; background: rgba(255,255,255,.92); }
.rc-team { width: 44px; height: 44px; box-sizing: border-box; padding: 3px; object-fit: contain; border-radius: 50%; background: rgba(255,255,255,.92); box-shadow: 0 1px 4px rgba(0,0,0,.5); }
.rc-team + .rc-team { margin-left: -12px; }
.rc-letter { width: 76px; height: 56px; display: flex; align-items: center; justify-content: center; border-radius: 10px; background: rgba(255,255,255,.1); font-size: 22px; font-weight: 800; }
.rc-title { font-size: 17px; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rc-sub { font-size: 13px; color: var(--text2); margin-top: 3px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.rc-sub2 { font-size: 12px; color: var(--text3); margin-top: 2px; }
.rc-rec { display: inline-flex; align-items: center; gap: 6px; padding: 2px 9px; border-radius: 999px; background: #e50914; color: #fff; font-size: 11px; font-weight: 800; }
.rc-rec i { width: 7px; height: 7px; border-radius: 50%; background: #fff; animation: rc-pulse 1.4s infinite; }
@keyframes rc-pulse { 50% { opacity: .25; } }
.rc-actions { display: flex; flex-wrap: wrap; gap: 8px; }
.rc-actions button:focus { outline: 3px solid #fff; outline-offset: 2px; }
html.zflix-tv-apk-density .rc-card { padding: 7px 10px; gap: 6px 10px; margin-bottom: 6px; border-radius: 8px; }
html.zflix-tv-apk-density .rc-art, html.zflix-tv-apk-density .rc-chan, html.zflix-tv-apk-density .rc-letter { width: 50px; height: 36px; }
html.zflix-tv-apk-density .rc-team { width: 28px; height: 28px; }
html.zflix-tv-apk-density .rc-title { font-size: 12px; }
html.zflix-tv-apk-density .rc-sub { font-size: 10px; margin-top: 2px; }
html.zflix-tv-apk-density .rc-sub2 { font-size: 9px; margin-top: 1px; }
html.zflix-tv-apk-density .rc-rec { font-size: 9px; padding: 1px 6px; }
html.zflix-tv-apk-density .rc-actions button { font-size: 10px !important; padding: 4px 9px !important; }
`;
const tint = (hex) => (/^[0-9a-f]{6}$/i.test(hex || '') ? `#${hex}38` : 'transparent');

/** Both teams for a game; the channel's logo for anything else; the first letter if neither is known. */
function Art({ item, title }) {
  const [broken, setBroken] = useState(false);
  const game = item?.game;
  if (game?.away?.logo && !broken) {
    return (
      <div className="rc-art">
        <img className="rc-team" src={game.away.logo} alt="" loading="lazy" onError={() => setBroken(true)} />
        <img className="rc-team" src={game.home.logo} alt="" loading="lazy" onError={() => setBroken(true)} />
      </div>
    );
  }
  if (item?.logo && !broken) return <div className="rc-art"><img className="rc-chan" src={item.logo} alt="" loading="lazy" onError={() => setBroken(true)} /></div>;
  return <div className="rc-letter">{String(title || '?').charAt(0)}</div>;
}

function Card({ item, title, sub, sub2, badge, onOpen, actions }) {
  const game = item?.game;
  return (
    <div className={`rc-card${badge === 'REC' ? ' is-rec' : ''}`} data-rec-row="true" style={game ? { backgroundImage: `linear-gradient(100deg, ${tint(game.away?.color)}, transparent 42%), linear-gradient(280deg, ${tint(game.home?.color)}, transparent 42%)` } : undefined}>
      <button type="button" className="rc-open" onClick={onOpen} disabled={!onOpen} style={{ cursor: onOpen ? 'pointer' : 'default' }}>
        <Art item={item} title={title} />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {badge === 'REC' ? <span className="rc-rec"><i />REC</span> : badge ? <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: 'rgba(255,255,255,0.14)', color: '#fff' }}>{badge}</span> : null}
            <span className="rc-title">{title}</span>
          </span>
          {sub ? <span className="rc-sub" style={{ display: 'block' }}>{sub}</span> : null}
          {sub2 ? <span className="rc-sub2" style={{ display: 'block' }}>{sub2}</span> : null}
        </span>
      </button>
      {actions ? <div className="rc-actions">{actions}</div> : null}
    </div>
  );
}

/** Locked recordings stay until they are unlocked: the oldest-first cleanup skips them and Delete is hidden. */
function LockButton({ rec, onToggle }) {
  if (!rec.canLock) return null;
  return (
    <button
      type="button"
      aria-pressed={rec.locked ? 'true' : 'false'}
      onClick={() => onToggle(rec)}
      style={{ padding: '8px 12px', borderRadius: 8, border: rec.locked ? '1px solid var(--accent)' : '1px solid var(--border)', background: 'transparent', color: rec.locked ? 'var(--text)' : 'var(--text2)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      {rec.locked ? 'Unlock' : 'Lock'}
    </button>
  );
}

/** A button that asks for a second press before doing something destructive (no pop-ups: they do not work on the TV app). */
function ConfirmButton({ label, confirmLabel, onConfirm }) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      onClick={() => (armed ? (setArmed(false), onConfirm()) : setArmed(true))}
      style={{ padding: '8px 12px', borderRadius: 8, border: armed ? '1px solid #ff6b6b' : '1px solid var(--border)', background: armed ? 'rgba(255,80,80,0.18)' : 'transparent', color: armed ? '#ffb3b3' : 'var(--text2)', fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}

export default function RecordingsView({ onPlay, onToast }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const dead = useRef(false);
  const rootRef = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/dvr', { credentials: 'include' });
      if (!r.ok) throw new Error('Could not load recordings');
      const j = await r.json();
      if (!dead.current) {
        setData(j);
        setErr('');
      }
    } catch (e) {
      if (!dead.current) setErr(e.message || 'Could not load recordings');
    }
  }, []);
  useEffect(() => {
    dead.current = false;
    load();
    const iv = setInterval(load, 15000);
    return () => {
      dead.current = true;
      clearInterval(iv);
    };
  }, [load]);

  const call = async (method, url, okMsg) => {
    try {
      const r = await fetch(url, { method, credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || 'That did not work');
      if (okMsg) onToast?.(okMsg, 'success');
    } catch (e) {
      onToast?.(e.message || 'That did not work', 'error');
    }
    load();
  };

  /** The remote: every card is one row, so Up / Down always reach the next one, however far apart its buttons sit. */
  useEffect(() => {
    const go = (el) => {
      el.focus({ preventScroll: true });
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return true;
    };
    window.__zflixOwnNav = (key, active) => {
      const root = rootRef.current;
      if (!root) return false;
      const rows = [...root.querySelectorAll('[data-rec-row]')].map((r) => [...r.querySelectorAll('button:not([disabled])')]).filter((b) => b.length);
      if (!rows.length) return false;
      if (!root.contains(active)) {
        const lost = !active || active === document.body;
        const fromHeader = key === 'ArrowDown' && active?.closest?.('[data-zflix-app-header="true"]');
        const fromMenu = key === 'ArrowRight' && active?.closest?.('.sidebar');
        return lost || fromHeader || fromMenu ? go(rows[0][0]) : false;
      }
      const ri = rows.findIndex((b) => b.includes(active));
      if (ri < 0) return false;
      const bi = rows[ri].indexOf(active);
      if (key === 'ArrowRight') return bi < rows[ri].length - 1 ? go(rows[ri][bi + 1]) : true;
      if (key === 'ArrowLeft') return bi > 0 ? go(rows[ri][bi - 1]) : false;
      const next = rows[ri + (key === 'ArrowDown' ? 1 : -1)];
      /** Up from the first row leaves for the top bar; Down from the last one stays put. */
      if (!next) return key === 'ArrowDown';
      return go(bi > 0 ? next[next.length - 1] : next[0]);
    };
    return () => {
      delete window.__zflixOwnNav;
    };
  }, []);

  const toggleLock = (rec) => call('POST', `/api/dvr/recordings/${rec.id}/${rec.locked ? 'unlock' : 'lock'}`, rec.locked ? 'Recording unlocked' : 'Locked: it will not be deleted');

  const play = async (rec) => {
    if (!rec.playUrl) return;
    /** Pick up where this profile stopped. */
    const saved = await fetch('/api/dvr/continue', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : []))
      .catch(() => []);
    onPlay({
      id: `dvr-${rec.id}`,
      recId: rec.id,
      resumeAt: (Array.isArray(saved) ? saved : []).find((x) => x.recId === rec.id)?.resumeAt,
      title: rec.episodeTitle ? `${rec.title} · ${rec.episodeTitle}` : rec.title,
      name: rec.title,
      overview: rec.overview,
      streamUrl: rec.playUrl,
      source: 'dvr',
      type: 'recording',
      growing: rec.status === 'recording',
    });
  };

  /** lockedBytes comes with the server that can lock; recordings run past a page update, so the two can arrive apart. */
  const recs = (data?.recordings || []).map((r) => ({ ...r, canLock: data.lockedBytes != null }));
  const now = recs.filter((r) => r.status === 'recording');
  const done = recs.filter((r) => r.status !== 'recording').sort((a, b) => b.start - a.start);
  const sched = data?.scheduled || [];
  const rules = data?.rules || [];
  const h = (t, n) => (
    <h3 style={{ fontSize: 15, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--text2)', margin: '22px 0 10px' }}>
      {t}
      {n != null ? <span style={{ color: 'var(--text3)', fontWeight: 400 }}> · {n}</span> : null}
    </h3>
  );

  return (
    <div ref={rootRef} style={{ maxWidth: 960 }}>
      <style>{CSS}</style>
      <h2 style={{ fontSize: 28, fontWeight: 800, margin: 0 }}>Recordings</h2>
      {data ? (
        <div style={{ marginTop: 6, fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
          {gb(Math.max(0, data.capBytes - data.usedBytes))} left
          <span style={{ fontWeight: 400, color: 'var(--text2)' }}> · {gb(data.usedBytes)} of {gb(data.capBytes)} used · oldest unlocked are removed when full</span>
        </div>
      ) : null}
      {data && data.lockedBytes > data.capBytes * 0.8 ? <div style={{ color: '#ffb3b3', marginTop: 10, fontSize: 14 }}>Locked recordings are using {gb(data.lockedBytes)}. Unlock or delete some, or new recordings will run out of room.</div> : null}
      {err ? <div style={{ color: '#ff8a8a', marginTop: 12 }}>{err}</div> : null}
      {!data && !err ? <div style={{ color: 'var(--text2)', marginTop: 16 }}>Loading…</div> : null}
      {data && !recs.length && !sched.length && !rules.length ? (
        <div style={{ color: 'var(--text2)', marginTop: 16, lineHeight: 1.6, maxWidth: 620 }}>
          Nothing recorded yet. In Live TV, open the guide and pick a show that has not started to record it (or the whole series), or press <b>Record</b> in the full-screen player to record what is on now.
        </div>
      ) : null}

      {now.length ? h('Recording now', now.length) : null}
      {now.map((r) => (
        <Card
          key={r.id}
          item={r}
          badge="REC"
          title={r.title}
          sub={[r.episodeTitle, r.channelName].filter(Boolean).join(' · ')}
          sub2={`Started ${fmtDay(r.startedAt || r.start)} · ${fmtLen(r.durationS)} so far · ends about ${fmtDay(r.end + (r.padAfterMs || 0))}`}
          onOpen={r.playUrl ? () => play(r) : null}
          actions={
            <>
              <LockButton rec={r} onToggle={toggleLock} />
              <ConfirmButton label="Stop" confirmLabel="Stop recording?" onConfirm={() => call('POST', `/api/dvr/stop/${r.id}`, 'Recording stopped')} />
            </>
          }
        />
      ))}

      {done.length ? h('Recorded', done.length) : null}
      {done.map((r) => (
        <Card
          key={r.id}
          item={r}
          badge={r.status === 'failed' ? 'FAILED' : r.locked ? 'LOCKED' : null}
          title={r.title}
          sub={[r.episodeTitle, r.channelName].filter(Boolean).join(' · ')}
          sub2={`${fmtDay(r.start)} · ${fmtLen(r.durationS)} · ${gb(r.sizeBytes)}`}
          onOpen={r.playUrl ? () => play(r) : null}
          actions={
            <>
              <LockButton rec={r} onToggle={toggleLock} />
              {r.locked ? null : <ConfirmButton label="Delete" confirmLabel="Delete it?" onConfirm={() => call('DELETE', `/api/dvr/recordings/${r.id}`, 'Recording deleted')} />}
            </>
          }
        />
      ))}

      {sched.length ? h('Scheduled', sched.length) : null}
      {sched.map((s) => (
        <Card
          key={s.id}
          item={s}
          badge={s.ruleId ? 'SERIES' : null}
          title={s.title}
          sub={[s.episodeTitle, s.channelName].filter(Boolean).join(' · ')}
          sub2={`${fmtDay(s.start)} – ${new Date(s.end).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}${s.padAfterMs > 10 * 60 * 1000 ? ' · +30 min for overtime' : ''}`}
          actions={<ConfirmButton label="Cancel" confirmLabel="Cancel it?" onConfirm={() => call('DELETE', `/api/dvr/scheduled/${s.id}`, 'Recording cancelled')} />}
        />
      ))}

      {rules.length ? h('Series', rules.length) : null}
      {rules.map((r) => (
        <Card
          key={r.id}
          item={r}
          title={r.title}
          sub={`${r.channelName || 'Channel'} · ${r.newOnly ? 'new episodes' : 'every airing'}`}
          sub2={r.checkedAt ? `Guide checked ${fmtDay(r.checkedAt)}` : 'Checking the guide…'}
          actions={<ConfirmButton label="Stop series" confirmLabel="Stop recording it?" onConfirm={() => call('DELETE', `/api/dvr/series/${r.id}`, 'Series recording stopped')} />}
        />
      ))}
    </div>
  );
}
